/**
 * The coding domain's FLAG MODULE (ruling 36 as amended 2026-08-29; converted
 * in flags fan-out batch 1).
 *
 * ONE DOOR, ONE FILE. The `defineFeatureFlag` descriptor and the getter that
 * reads it are CO-LOCATED here, and `@heidi/coding/flags` publishes both.
 * `/contributions` carries nothing for flags.
 *
 * THIS DOMAIN IS WHY THE BATCH EXISTS. `contributions/feature-flags.ts` was the
 * only `/contributions` flag member repo-wide — it declared the descriptor in
 * the contributions door while the READ stayed app-side, which was the ruling as
 * first written. The amendment supersedes exactly that arrangement, for three
 * reasons on the record: the shell registry's `featureFlags` aggregation arm has
 * no production reader, a flag is the one descriptor class the declaring domain
 * itself consumes, and reaching a domain's flag surface through the
 * contributions barrel is the graph-contamination path that turned five cases in
 * `src/tauri/__tests__/ipc.test.ts` red on `cva`. That file's own docblock had
 * already recorded that staying app-side was "a choice this domain has not
 * made", and that the getter "composes nothing, so it could" move. This is the
 * move; `git log --follow` on this path traces back through it.
 *
 * The `./flags` door also NARROWED the app's graph rather than widening it. The
 * app barrel used to write `import { clinicalCodingDashboardFlag } from
 * '@heidi/coding/contributions'` to read one key, so it pulled coding's whole
 * contributions door — `settings.ts` included — into its own import graph.
 *
 * `defineFeatureFlag` comes from `@heidi/runtime/feature-flags` rather than
 * `@heidi/runtime/shell` so this module binds ONE door. `@heidi/runtime/shell`
 * still exports the factory, so nothing that declares a descriptor elsewhere
 * changes; what this buys is that the guard admits the flags door only from a
 * package's DECLARED getter module, so reaching the factory through it is
 * possible only where the amendment says descriptors belong.
 *
 * ONE MINTING SITE PER FLAG, made structural rather than conventional by
 * co-location: the getter reads `descriptor.key` / `descriptor.defaultValue`
 * from the declaration directly above it, so the pair cannot drift, and
 * `flags.test.ts` asserts each key appears exactly once in this file and that no
 * read takes a string literal — the only assertion that can tell a descriptor
 * read from a hardcoded string that happens to be equal today.
 *
 * THE DOOR IS HEADLESS, guarded transitively rather than by inspection.
 * `scripts/monorepo-migration/lib/package-ui-zone.ts` admits
 * `@heidi/runtime/feature-flags` ONLY from this file out of everything under
 * `packages/coding/src`; `manifest-boundary.test.ts` walks this module's graph
 * ACROSS package boundaries and refuses `@heidi/design-system` and this
 * package's own `/ui` zone anywhere in it, and separately proves the door
 * imports in a bare Node process with no DOM and no side effects.
 *
 * PACKAGE UI IMPORTS THIS RELATIVELY (`../flags`), never through the door — a
 * package that imports itself by name resolves through node_modules and reads as
 * an undeclared external dependency. The app barrel `@/features/flags`
 * re-exports the getter from `@heidi/coding/flags`, so its three consumers and
 * their `jest.mock` literals are untouched.
 */
import { defineFeatureFlag, useBooleanFlag } from '@heidi/runtime/feature-flags';

// ---------------------------------------------------------------------------
// The declaration. `key` is a FROZEN EXTERNAL IDENTITY — Flagsmith targets the
// flag by it, so renaming it IS switching flags, never a refactor.
// ---------------------------------------------------------------------------

/**
 * Clinical coding dashboard / case surfaces.
 *
 * `builds: 'all'` carried forward unchanged from the contributions declaration,
 * for the reason `codingSettingsScreen` measured: the dashboard and case
 * surfaces are clinical faces of the same subtree, which ships on every build
 * profile, so the D6 default (`'cloud-only'`) would withhold the flag from the
 * on-prem appliance for surfaces that ship there today.
 */
export const clinicalCodingDashboardFlag = defineFeatureFlag({
  key: 'release-msh-clinical-coding',
  defaultValue: false,
  description: 'Clinical coding dashboard / case surfaces',
  builds: 'all',
});

export const scribeCodingSidebarV2Flag = defineFeatureFlag({
  key: 'release-scribe-coding-sidebar-v2',
  defaultValue: false,
  description: 'Scribe coding sidebar V2',
  builds: 'all',
});

// ---------------------------------------------------------------------------
// The getter. Reads the pair off the descriptor above it.
// ---------------------------------------------------------------------------

/**
 * Clinical coding dashboard / case surfaces. Three app consumers, all of which
 * keep importing this from `@/features/flags`: the sidebar nav item, the
 * dashboard page and the case page.
 *
 * One disclosed behavioural delta, and it is a gain rather than a risk:
 * `useBooleanFlag` honours the injected `forceEnableAll` override, which the
 * `useBooleanFlagValue` call it replaces did not. That is the choke point the
 * door exists for (design D8) and the knob is dev-only — recorded here rather
 * than discovered later.
 */
export const useIsClinicalCodingDashboardEnabled = (): boolean =>
  useBooleanFlag(
    clinicalCodingDashboardFlag.key,
    clinicalCodingDashboardFlag.defaultValue
  );

export const useIsScribeCodingSidebarV2Enabled = (): boolean =>
  useBooleanFlag(scribeCodingSidebarV2Flag.key, scribeCodingSidebarV2Flag.defaultValue);
